/**
 * La cola de mutaciones (docs/esquema.md §5.1 + endurecimiento de la ronda del
 * plan): cada fila del outbox lleva SU {epoch, seq}; los metadatos
 * {epoch, proximo_seq} viven en un único registro (config.cola) y el alta es
 * UNA transacción que abarca metadatos + outbox + borradores — el seq no puede
 * duplicarse entre pestañas (IndexedDB serializa las readwrite sobre el mismo
 * store) ni puede quedar un registro «guardado» a medias. Un epoch nuevo SOLO
 * se genera con el outbox vacío; si faltan los metadatos pero hay filas, se
 * reconstruyen DESDE las filas (jamás se re-etiqueta una fila).
 *
 * v3: el outbox está tipado por entidad (viaje de verde o descarte de
 * recepción) y los recibos se guardan por RAÍZ, que el acuse del servidor trae.
 */

import { generarUlid } from '../../src/ulid';
import { mutacionesAProyectar, type MutacionPendiente, type RegistroDeProduccion } from '../nucleo/produccion';
import { fechaDeProduccion } from '../nucleo/tiempo';
import type { FilaPendiente } from '../nucleo/embolsado';
import { alCompletar, ENTIDADES_AVISOS, ENTIDADES_EMBOLSADO, ENTIDADES_PRODUCCION, ENTIDADES_PROYECTADAS, promesa, type Entidad, type FilaOutbox, type CadenaDeMuestra, type MetadatosCola, type Recibo, type Resumen } from './bd';

export interface MutacionAEncolar {
	id: string;
	tipo: string;
	payload: unknown;
	entidad: Entidad;
	registro_id: string;
	personas: string[];
	resumen: Resumen;
	/** La MUTACIÓN de este teléfono de la que depende (§5.2 r. 14, §5.7 r. 20): la apertura del silo de un parcial, la del pallet de una carga. */
	padre_id?: string;
}

/** Cuántos ids de mutaciones caídas se recuerdan (`config.caidas`): sobra para meses. */
const TOPE_CAIDAS = 500;

/**
 * Los ids de las mutaciones que el servidor no aceptó, incluso después de que el
 * encargado las acusó y salieron de la bandeja. Un borrador que nació dependiendo
 * de una de ellas puede confirmarse mucho después: si su padre ya no está en la
 * cola, la regla 14 no tenía cómo saber que había caído, el hijo viajaba y el
 * servidor lo dejaba «pendiente» para siempre (Codex, ronda del 11/9).
 */
async function caidasEnTx(config: IDBObjectStore): Promise<string[]> {
	return ((await promesa(config.get('caidas'))) as string[] | undefined) ?? [];
}

function recordarCaidas(config: IDBObjectStore, previas: string[], nuevas: Iterable<string>): void {
	const todas = [...previas, ...nuevas];
	config.put(todas.slice(Math.max(0, todas.length - TOPE_CAIDAS)), 'caidas');
}

/**
 * Encola 1..N mutaciones con seqs consecutivos y elimina los borradores, todo
 * o nada. Un hijo cuyo padre ya cayó —rechazado o en conflicto, esté todavía en
 * la bandeja o no— nace en la bandeja con `padre_rechazado`, sin viajar; y arrastra
 * a lo que dependa de él dentro del mismo lote.
 */
/** Un rótulo elegido que otra fila pendiente de la cola ya lleva (§5.7 r. 22): se vuelve al paso del rótulo. */
export class RotuloEnCola extends Error {
	constructor(readonly numero: number) {
		super(`el rótulo ${numero} ya está en una apertura pendiente`);
	}
}

/** El incidente que se iba a rehacer ya no está en la bandeja: otra pestaña lo rehízo o el encargado lo quitó (Codex, 15/9). */
export class IncidenteResuelto extends Error {
	constructor(readonly id: string) {
		super(`el incidente ${id} ya no está en la bandeja`);
	}
}

/**
 * El borrador que se confirma ya se confirmó desde otra pestaña —y su registro pudo tener acuse y
 * salir de la cola—: una copia vieja en memoria lo volvía a guardar y a encolar con los mismos ids y
 * otro contenido, y el servidor lo devolvía como `id_reusado` (Codex, ronda de la v5 entera, 15/9).
 */
export class BorradorYaConfirmado extends Error {
	constructor(readonly captureId: string) {
		super(`el borrador ${captureId} ya se confirmó`);
	}
}

/** Cuántos `capture_id` confirmados se recuerdan: los últimos, para que una copia vieja no reviva uno. */
const CAPTURAS_RECORDADAS = 200;

/**
 * Encola en UNA transacción: las mutaciones, el borrado de los borradores que las produjeron
 * y —si la hay— la confirmación de la foto del precinto (`fotosAConfirmar`): sin apertura
 * encolada no hay foto confirmada, y al revés (§5.3; Codex, ronda del diff, 14/9). Al rehacer una
 * apertura con otro rótulo (r. 22), `incidentesAQuitar` saca de la bandeja lo que se reemplaza en
 * la misma transacción. Y una apertura cuyo rótulo ya lleva otra fila pendiente no entra:
 * `RotuloEnCola`, sin escribir nada.
 */
export async function encolar(
	bd: IDBDatabase,
	mutaciones: MutacionAEncolar[],
	borradoresAEliminar: string[],
	creadoEn: string,
	fotosAConfirmar: string[] = [],
	incidentesAQuitar: string[] = [],
): Promise<void> {
	const aperturas = mutaciones.filter((m) => m.tipo === 'pallet.abrir');
	const tx = bd.transaction(
		['config', 'outbox', 'borradores', ...(fotosAConfirmar.length > 0 ? ['fotos'] : []), ...(aperturas.length > 0 ? ['recibos'] : [])],
		'readwrite',
	);
	const config = tx.objectStore('config');
	const outbox = tx.objectStore('outbox');

	const metadatos = await metadatosEnTx(config, outbox);
	const caidas = new Set(await caidasEnTx(config));
	const nacidasCaidas: string[] = [];

	// El rótulo de una apertura nueva contra las pendientes de ahora Y contra las demás aperturas del
	// mismo lote: dos borradores del mismo teléfono que eligieron el mismo papel —o un rehacer que
	// tomó el número de otra apertura que reencola— no pueden viajar los dos (el segundo iría a
	// `rotulo_usado`; Codex, ronda del diff de 7b, 15/9). Y contra los recibos de pallets de la misma
	// campaña: la apertura de la otra pestaña pudo tener acuse un segundo antes y ya no estar en la
	// cola, con el catálogo todavía sin reflejarla (Codex, ronda de la v5 entera, 15/9).
	if (aperturas.length > 0) {
		const pendientes = ((await promesa(outbox.getAll())) as FilaOutbox[]).filter((f) => f.estado_local === 'pendiente' && f.tipo === 'pallet.abrir');
		const acusados = ((await promesa(tx.objectStore('recibos').getAll())) as Recibo[]).filter((r) => r.entidad === 'pallet');
		const vistos = new Set<string>();
		for (const a of aperturas) {
			const numero = numeroDe(a.payload);
			if (numero === null) continue;
			const campania = campoDe(a.payload, 'campania_id');
			const clave = `${campania}/${numero}`;
			const enCola = pendientes.some((f) => numeroDe(f.payload) === numero && campoDe(f.payload, 'campania_id') === campania);
			const acusado = acusados.some((r) => (r.numero ?? numeroDe(r.payload)) === numero && campoDe(r.payload, 'campania_id') === campania);
			if (vistos.has(clave) || enCola || acusado) {
				tx.abort();
				throw new RotuloEnCola(numero);
			}
			vistos.add(clave);
		}
	}
	// Al rehacer, el incidente que se reemplaza tiene que seguir en la bandeja: si otra pestaña ya lo
	// rehízo (o el encargado lo quitó), no se reencola dos veces la misma identidad.
	for (const [i, id] of incidentesAQuitar.entries()) {
		const fila = (await promesa(outbox.get(id))) as FilaOutbox | undefined;
		if (fila && fila.estado_local !== 'pendiente') outbox.delete(id);
		else if (i === 0) {
			tx.abort();
			throw new IncidenteResuelto(id);
		}
	}
	// El borrador que se consume, contra los ya consumidos (en la misma transacción que los encola): una
	// copia vieja en otra pestaña no vuelve a mandar lo mismo con otro contenido.
	const consumidas = ((await promesa(config.get('capturas_consumidas'))) as string[] | undefined) ?? [];
	const repetida = borradoresAEliminar.find((captureId) => consumidas.includes(captureId));
	if (repetida !== undefined) {
		tx.abort();
		throw new BorradorYaConfirmado(repetida);
	}
	let seq = metadatos.proximo_seq;
	for (const mutacion of mutaciones) {
		const padreCaido = mutacion.padre_id !== undefined && (caidas.has(mutacion.padre_id) || (await padreCaidoEnBandeja(outbox, mutacion.padre_id)));
		if (padreCaido) {
			caidas.add(mutacion.id);
			nacidasCaidas.push(mutacion.id);
		}
		const fila: FilaOutbox = {
			id: mutacion.id,
			epoch: metadatos.epoch,
			seq,
			tipo: mutacion.tipo,
			payload: mutacion.payload,
			creado_en: creadoEn,
			entidad: mutacion.entidad,
			registro_id: mutacion.registro_id,
			personas: mutacion.personas,
			...(mutacion.padre_id === undefined ? {} : { padre_id: mutacion.padre_id }),
			estado_local: padreCaido ? 'rechazada' : 'pendiente',
			motivo: padreCaido ? 'padre_rechazado' : null,
			resumen: mutacion.resumen,
		};
		outbox.add(fila);
		seq += 1;
	}
	config.put({ epoch: metadatos.epoch, proximo_seq: seq } satisfies MetadatosCola, 'cola');
	if (borradoresAEliminar.length > 0) config.put([...consumidas, ...borradoresAEliminar].slice(-CAPTURAS_RECORDADAS), 'capturas_consumidas');
	if (nacidasCaidas.length > 0) recordarCaidas(config, [...caidas].filter((id) => !nacidasCaidas.includes(id)), nacidasCaidas);
	for (const captureId of borradoresAEliminar) tx.objectStore('borradores').delete(captureId);
	if (fotosAConfirmar.length > 0) {
		const fotos = tx.objectStore('fotos');
		for (const fotoId of fotosAConfirmar) {
			const foto = (await promesa(fotos.get(fotoId))) as { confirmada?: boolean } | undefined;
			if (foto && !foto.confirmada) fotos.put({ ...foto, confirmada: true });
		}
	}
	await alCompletar(tx);
}

/** ¿El padre sigue en la bandeja como rechazado o en conflicto? (Pendiente no es caído: se espera.) */
async function padreCaidoEnBandeja(outbox: IDBObjectStore, padreId: string): Promise<boolean> {
	const padre = (await promesa(outbox.get(padreId))) as FilaOutbox | undefined;
	return padre !== undefined && padre.estado_local !== 'pendiente';
}

function numeroDe(payload: unknown): number | null {
	const valor = typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>).numero : undefined;
	return typeof valor === 'number' ? valor : null;
}

function campoDe(payload: unknown, clave: string): string | null {
	const valor = typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>)[clave] : undefined;
	return typeof valor === 'string' ? valor : null;
}

/**
 * Un incidente de la bandeja con todo lo que colgaba de él (hijos, nietos…), en el orden de la
 * cola: lo que se vuelve a encolar al rehacer una apertura con otro rótulo (§5.7 r. 22). Lo que
 * dependía de la apertura cayó con ella (`padre_rechazado`) y sigue en el outbox. Se sigue la
 * cadena de padres Y la identidad del pallet: si el encargado quitó de la bandeja una carga del
 * medio, el cierre que colgaba de ella sigue siendo de este pallet y vuelve igual (Codex, 15/9).
 * `enCola`: todos los ids del outbox, para saber qué padres siguen existiendo.
 */
export async function incidenteConDescendientes(bd: IDBDatabase, id: string): Promise<{ incidente: FilaOutbox; hijos: FilaOutbox[]; enCola: Set<string> } | null> {
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	const incidente = filas.find((f) => f.id === id && f.estado_local !== 'pendiente');
	if (!incidente) return null;
	const enOrden = [...filas].sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch.localeCompare(b.epoch)));
	// Los pallets del árbol: el de la apertura caída y los que se abrieron colgando de ella (un segundo
	// pallet de la misma letra y formato espera al cierre del primero): sus cargas y cierres vuelven
	// aunque el encargado haya quitado del medio la fila de la que colgaban (Codex, ronda de la v5
	// entera, 15/9: solo se seguía la identidad del primero).
	const pallets = new Set<string>();
	const palletId = campoDe(incidente.payload, 'pallet_id');
	if (palletId !== null) pallets.add(palletId);
	const dentro = new Set([id]);
	const cargas = new Set<string>();
	const hijos: FilaOutbox[] = [];
	const esDelArbol = (f: FilaOutbox) => {
		if (f.tipo.startsWith('pallet.') || f.tipo === 'envasado.crear') return pallets.has(campoDe(f.payload, 'pallet_id') ?? '');
		if (f.tipo === 'envasado.corregir' || f.tipo === 'carga.corregir') return cargas.has(campoDe(f.payload, 'corrige_a') ?? '');
		if (f.tipo === 'envasado.anular' || f.tipo === 'carga.anular') return cargas.has(campoDe(f.payload, 'envasado_id') ?? '');
		return false;
	};
	// Hasta punto fijo, en orden de cola: el padre siempre va antes que el hijo.
	let cambio = true;
	while (cambio) {
		cambio = false;
		for (const f of enOrden) {
			if (dentro.has(f.id) || f.estado_local === 'pendiente') continue;
			if (!(f.padre_id !== undefined && dentro.has(f.padre_id)) && !esDelArbol(f)) continue;
			dentro.add(f.id);
			if (f.tipo === 'envasado.crear' || f.tipo === 'envasado.corregir' || f.tipo === 'carga.corregir') cargas.add(campoDe(f.payload, 'envasado_id') ?? '');
			if (f.tipo === 'pallet.abrir') pallets.add(campoDe(f.payload, 'pallet_id') ?? '');
			hijos.push(f);
			cambio = true;
		}
	}
	return {
		incidente,
		hijos: hijos.sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch.localeCompare(b.epoch))),
		enCola: new Set(filas.map((f) => f.id)),
	};
}

/** Metadatos de cola DENTRO de la transacción de alta; reconstruye o inicializa. */
async function metadatosEnTx(config: IDBObjectStore, outbox: IDBObjectStore): Promise<MetadatosCola> {
	const guardados = (await promesa(config.get('cola'))) as MetadatosCola | undefined;
	if (guardados) return guardados;
	const filas = (await promesa(outbox.getAll())) as FilaOutbox[];
	if (filas.length === 0) {
		return { epoch: generarUlid(), proximo_seq: 1 };
	}
	// Estado anómalo (metadatos perdidos con cola viva): se reconstruye desde las
	// filas — el epoch de la fila de mayor seq y el seq siguiente. Jamás epoch nuevo.
	const ultima = filas.reduce((a, b) => (b.seq > a.seq ? b : a));
	return { epoch: ultima.epoch, proximo_seq: ultima.seq + 1 };
}

/** Pendientes de envío, agrupadas por epoch y ordenadas por seq (el wire lleva UN epoch por request). */
export async function pendientesPorEpoch(bd: IDBDatabase): Promise<Array<{ epoch: string; filas: FilaOutbox[] }>> {
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	const pendientes = filas.filter((f) => f.estado_local === 'pendiente');
	const porEpoch = new Map<string, FilaOutbox[]>();
	for (const fila of pendientes) {
		const grupo = porEpoch.get(fila.epoch) ?? [];
		grupo.push(fila);
		porEpoch.set(fila.epoch, grupo);
	}
	return [...porEpoch.entries()]
		.map(([epoch, grupo]) => ({ epoch, filas: grupo.sort((a, b) => a.seq - b.seq) }))
		.sort((a, b) => (a.epoch < b.epoch ? -1 : 1)); // epochs viejos (ULID menor) primero
}

/**
 * Lo que hay que aplicarle al catálogo para que la pantalla de producción diga la
 * verdad: los efectos ya confirmados que el snapshot todavía no refleja, más los
 * pendientes. El orden es el de aplicación (primero los confirmados).
 */
export async function mutacionesDeProduccion(bd: IDBDatabase, horaDelCatalogo: string | undefined): Promise<MutacionPendiente[]> {
	return mutacionesDeEntidades(bd, horaDelCatalogo, ENTIDADES_PRODUCCION);
}

/** v6 (§12.3): los viajes de este teléfono que el catálogo todavía no refleja, más los pendientes. */
export async function mutacionesDeVerde(bd: IDBDatabase, horaDelCatalogo: string | undefined): Promise<MutacionPendiente[]> {
	return mutacionesDeEntidades(bd, horaDelCatalogo, new Set<Entidad>(['viaje_verde']));
}

/** v6 (§12.2, §12.4): los avisos y las notas que el catálogo todavía no refleja, más los pendientes. */
export async function mutacionesDeAvisos(bd: IDBDatabase, horaDelCatalogo: string | undefined): Promise<MutacionPendiente[]> {
	return mutacionesDeEntidades(bd, horaDelCatalogo, ENTIDADES_AVISOS);
}

/**
 * Todo lo de producción que este teléfono tiene, recibos (30 días) y cola, sin recortar por el
 * catálogo y con su resumen: de ahí salen los silos ya cerrados (2/10). Primero los recibos por
 * acuse, después los pendientes por orden de cola.
 */
export async function historiaDeProduccion(bd: IDBDatabase): Promise<RegistroDeProduccion[]> {
	const tx = bd.transaction(['outbox', 'recibos']);
	const filas = (await promesa(tx.objectStore('outbox').getAll())) as FilaOutbox[];
	const comprobantes = (await promesa(tx.objectStore('recibos').getAll())) as Recibo[];
	const confirmados = comprobantes
		.filter((r) => ENTIDADES_PRODUCCION.has(r.entidad))
		.sort((a, b) => a.sincronizado_en.localeCompare(b.sincronizado_en))
		.map((r) => ({ tipo: r.tipo, payload: r.payload, resumen: r.resumen }));
	const pendientes = filas
		.filter((f) => f.estado_local === 'pendiente' && ENTIDADES_PRODUCCION.has(f.entidad))
		.sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch.localeCompare(b.epoch)))
		.map((f) => ({ tipo: f.tipo, payload: f.payload, resumen: f.resumen }));
	return [...confirmados, ...pendientes];
}

/** Lo mismo para calidad: las muestras que el catálogo de «sin muestra» todavía no descuenta. */
export async function mutacionesDeCalidad(bd: IDBDatabase, horaDelCatalogo: string | undefined): Promise<MutacionPendiente[]> {
	return mutacionesDeEntidades(bd, horaDelCatalogo, new Set<Entidad>(['muestra']));
}

/** Y para embolsado (v5, §5.7 r. 21): pallets y cargas que `pallets_abiertos` todavía no refleja, más los pendientes. */
export async function mutacionesDeEmbolsado(bd: IDBDatabase, horaDelCatalogo: string | undefined): Promise<MutacionPendiente[]> {
	return mutacionesDeEntidades(bd, horaDelCatalogo, ENTIDADES_EMBOLSADO);
}

/** Las filas pendientes de esas entidades, en orden de cola: de acá se elige el `padre_id` de una mutación nueva (§5.7 r. 20). */
export async function pendientesDe(bd: IDBDatabase, entidades: ReadonlySet<Entidad>): Promise<FilaPendiente[]> {
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	return filas
		.filter((f) => f.estado_local === 'pendiente' && entidades.has(f.entidad))
		.sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch.localeCompare(b.epoch)))
		.map((f) => ({ id: f.id, epoch: f.epoch, seq: f.seq, tipo: f.tipo, payload: f.payload, ...(f.padre_id === undefined ? {} : { padre_id: f.padre_id }) }));
}

/** Las de embolsado (pallets y cargas). */
export async function pendientesDeEmbolsado(bd: IDBDatabase): Promise<FilaPendiente[]> {
	return pendientesDe(bd, ENTIDADES_EMBOLSADO);
}

async function mutacionesDeEntidades(bd: IDBDatabase, horaDelCatalogo: string | undefined, entidades: Set<Entidad>): Promise<MutacionPendiente[]> {
	const tx = bd.transaction(['outbox', 'recibos']);
	const filas = (await promesa(tx.objectStore('outbox').getAll())) as FilaOutbox[];
	const comprobantes = (await promesa(tx.objectStore('recibos').getAll())) as Recibo[];
	const pendientes = filas
		.filter((f) => f.estado_local === 'pendiente' && entidades.has(f.entidad))
		.sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch.localeCompare(b.epoch)))
		.map((f) => ({ tipo: f.tipo, payload: f.payload, mutacion_id: f.id, resumen: f.resumen }));
	return mutacionesAProyectar(
		comprobantes.filter((r) => entidades.has(r.entidad)),
		pendientes,
		horaDelCatalogo,
	);
}

/**
 * v6: lo que el recibo de una versión de muestra hereda del que pisa (`Recibo.cadena`): las bases de
 * los cambios de letra y humedades anteriores de la misma raíz, y la última letra elegida en ellos.
 * Un `muestra.crear` no es puente de nada: el catálogo lo refleja con su propio id. §12.17: una
 * corrección de la humedad también es puente, y su letra es conocida (viaja con `regla_version`, así
 * que la que calculó el teléfono es la que escribió el servidor): va en el resumen.
 */
function cadenaDeMuestra(previo: Recibo | undefined): CadenaDeMuestra | undefined {
	if (previo === undefined) return undefined;
	// §12.17: la hoja de este teléfono no es puente de nada, pero su jornada se conserva.
	if (previo.tipo === 'muestra.crear') return { bases: [], letra: null, hoja: previo.resumen.fecha };
	// §12.18: la corrección de la hoja también es puente, y su letra viaja en el payload (con `regla_version`: es la que se escribió).
	if (previo.tipo !== 'muestra.letra' && previo.tipo !== 'muestra.humedad' && previo.tipo !== 'muestra.corregir_humedad' && previo.tipo !== 'muestra.corregir') return undefined;
	const p = previo.payload as { corrige_a?: unknown; letra_asignada?: unknown } | undefined;
	const bases = [...(previo.cadena?.bases ?? []), ...(typeof p?.corrige_a === 'string' ? [p.corrige_a] : [])];
	const letra =
		(previo.tipo === 'muestra.letra' || previo.tipo === 'muestra.corregir') && typeof p?.letra_asignada === 'string'
			? p.letra_asignada
			: previo.tipo === 'muestra.corregir_humedad' && previo.resumen.entidad === 'muestra'
				? previo.resumen.letra
				: (previo.cadena?.letra ?? null);
	return { bases, letra, ...(previo.cadena?.hoja === undefined ? {} : { hoja: previo.cadena.hoja }) };
}

export interface ResultadoDelServer {
	id: string;
	estado: 'aplicada' | 'duplicada' | 'rechazada' | 'conflicto' | 'pendiente';
	motivo?: string;
	nro_viaje?: number;
	nro_muestra?: number;
	/** v5: el número del pallet, en el acuse de `pallet.abrir`. */
	numero?: number;
	raiz_id?: string;
	/** Solo en `duplicada`: cuándo se aplicó la original; el recibo se fecha ahí y no en el reintento (§5.2.1). */
	procesada_en?: string;
}

/**
 * Aplica los ACK de un lote en UNA transacción: aplicada/duplicada sale del
 * outbox y deja un recibo por (entidad, raíz) —una corrección reemplaza el de
 * su raíz, una anulación lo borra—; rechazada/conflicto queda en bandeja (no se
 * reenvía); pendiente se retiene tal cual para el próximo sync.
 *
 * La raíz viene en el acuse. Un registro recién creado es su propia raíz, así
 * que si el servidor no la mandara se usa el id del registro; para una
 * corrección o una anulación sin raíz no hay forma de saber qué recibo tocar,
 * y la fila se RETIENE (queda pendiente, visible) en vez de perderse.
 */
export async function marcarResultados(bd: IDBDatabase, resultados: ResultadoDelServer[], sincronizadoEn: string): Promise<void> {
	const tx = bd.transaction(['config', 'outbox', 'recibos'], 'readwrite');
	const outbox = tx.objectStore('outbox');
	const recibos = tx.objectStore('recibos');
	/** Registros que el servidor no aceptó: sus hijos ya no tienen a dónde ir. */
	const caidos = new Set<string>();
	for (const resultado of resultados) {
		const fila = (await promesa(outbox.get(resultado.id))) as FilaOutbox | undefined;
		if (!fila) continue;
		if (resultado.estado === 'aplicada' || resultado.estado === 'duplicada') {
			// `llenado.abrir` crea la identidad del silo aunque no se llame «.crear»: sin
			// esto, un acuse sin `raiz_id` retendría la fila para siempre.
			const esCreacion = fila.tipo.endsWith('.crear') || fila.tipo === 'llenado.abrir' || fila.tipo === 'pallet.abrir';
			const esAnulacion = fila.tipo.endsWith('.anular');
			const raiz = resultado.raiz_id ?? (esCreacion ? fila.registro_id : null);
			if (raiz === null) {
				console.error('sync: acuse sin raíz para una corrección o anulación; se retiene', fila.id);
				continue;
			}
			outbox.delete(fila.id);
			// Un replay (`duplicada`) se fecha cuando se aplicó la primera vez, no ahora: si no, el recibo
			// «rejuvenecía», superaba a un snapshot que ya lo refleja y volvía a proyectar un pallet o un silo
			// que gestión pudo cerrar entre medio (Codex, ronda de la v5 entera, 15/9).
			const fechadoEn = resultado.estado === 'duplicada' && resultado.procesada_en !== undefined ? resultado.procesada_en : sincronizadoEn;
			if (ENTIDADES_EMBOLSADO.has(fila.entidad)) {
				const previo = (await promesa(recibos.get([fila.entidad, raiz]))) as Recibo | undefined;
				recibos.put(reciboDeEmbolsado(previo, fila, raiz, resultado, fechadoEn));
			} else if (esAnulacion) {
				recibos.delete([fila.entidad, raiz]);
			} else {
				const cadena = fila.entidad === 'muestra' ? cadenaDeMuestra((await promesa(recibos.get([fila.entidad, raiz]))) as Recibo | undefined) : undefined;
				recibos.put({
					raiz_id: raiz,
					entidad: fila.entidad,
					registro_id: fila.registro_id,
					...(ENTIDADES_PROYECTADAS.has(fila.entidad) ? { tipo: fila.tipo, payload: fila.payload } : {}),
					...(cadena === undefined ? {} : { cadena }),
					nro_viaje: resultado.nro_viaje ?? null,
					...(resultado.nro_muestra === undefined ? {} : { nro_muestra: resultado.nro_muestra }),
					personas: fila.personas,
					resumen: fila.resumen,
					sincronizado_en: fechadoEn,
				} satisfies Recibo);
			}
		} else if (resultado.estado === 'rechazada' || resultado.estado === 'conflicto') {
			outbox.put({ ...fila, estado_local: resultado.estado, motivo: resultado.motivo ?? null });
			caidos.add(fila.id);
		}
		// 'pendiente': se retiene sin cambios.
	}
	// Regla 14 (§5.2): si el padre no entró, el hijo no tiene dónde colgarse. Va a la
	// bandeja SIN viajar, con un motivo que se entiende: si viajara, el servidor lo
	// dejaría «pendiente» para siempre y nadie lo miraría.
	if (caidos.size > 0) {
		const todas = (await promesa(outbox.getAll())) as FilaOutbox[];
		// Hasta punto fijo: el cierre depende del parcial, que depende de la apertura.
		// Si cae la apertura tienen que caer los dos, no solo el hijo directo.
		let cambio = true;
		while (cambio) {
			cambio = false;
			for (const fila of todas) {
				if (fila.estado_local !== 'pendiente' || fila.padre_id === undefined) continue;
				if (!caidos.has(fila.padre_id) || caidos.has(fila.id)) continue;
				outbox.put({ ...fila, estado_local: 'rechazada', motivo: 'padre_rechazado' });
				caidos.add(fila.id);
				cambio = true;
			}
		}
		// Y se recuerdan: un borrador que dependa de ellas puede confirmarse cuando ya
		// salieron de la bandeja.
		const config = tx.objectStore('config');
		recordarCaidas(config, await caidasEnTx(config), caidos);
	}
	await alCompletar(tx);
}

/**
 * v5 (§5.7 r. 20–21): el recibo de un pallet o de una carga. La pantalla de embolsado se
 * proyecta POR IDENTIDAD sobre el catálogo, así que el recibo tiene que seguir diciendo qué es
 * el registro después de cualquier acuse: la apertura trae el número del pallet y las
 * transiciones siguientes lo conservan (va al rótulo); una corrección se FUNDE sobre el
 * comando previo de su raíz —la apertura o la carga— en vez de reemplazarlo, porque su
 * payload solo trae lo que cambió; una transición del pallet (cerrar, anular, unir) conserva
 * debajo lo que el recibo previo sabía —letra, formato, campaña, apertura— porque el cierre solo
 * trae su hora y su testigo, y una carga que el catálogo todavía no refleja necesita el formato
 * de su pallet para descontar del precinto (Codex, ronda de la v5 entera, 15/9; sin recibo previo,
 * la identidad sale del resumen del propio cierre); y una anulación se conserva como tal hasta
 * que el snapshot la refleje (verde y producción la borran: allí no sostiene ninguna pantalla;
 * acá borrarla resucitaría la carga anulada hasta el refresco siguiente).
 */
function reciboDeEmbolsado(previo: Recibo | undefined, fila: FilaOutbox, raiz: string, resultado: ResultadoDelServer, sincronizadoEn: string): Recibo {
	const funde = fila.tipo.endsWith('.corregir') && previo?.tipo !== undefined && esObjeto(previo.payload) && esObjeto(fila.payload);
	// §12.18: `carga.corregir` solo cambia las bolsas; su `persona_id` es quien corrige y no se funde sobre quien cargó.
	const cambios = fila.tipo === 'carga.corregir' && esObjeto(fila.payload) ? { envasado_id: fila.payload.envasado_id, cant_bolsas: fila.payload.cant_bolsas } : fila.payload;
	// El número: del acuse de la apertura, del recibo previo (§5.1), o del resumen del cierre de un
	// pallet que vino del catálogo ya numerado —no hay recibo previo del que heredarlo—.
	const delResumen = fila.resumen.entidad === 'pallet' && typeof fila.resumen.numero === 'number' ? fila.resumen.numero : undefined;
	const numero = resultado.numero ?? previo?.numero ?? delResumen;
	const conserva = fila.entidad === 'pallet' && !funde && esObjeto(fila.payload);
	const identidad: Record<string, unknown> =
		fila.resumen.entidad === 'pallet' ? { es_tarima: fila.resumen.es_tarima ? 1 : 0, letra: fila.resumen.letra, formato_bolsa_g: fila.resumen.formato_bolsa_g } : {};
	// §12.21: lo que una unión de a partes trae (bolsas, el testigo, la parte) se conserva para proyectar hasta que el
	// catálogo la refleje; pero no se hereda a la unión SIGUIENTE de la misma tarima, que si va sin `bolsas` es entera.
	const previoConservado = esObjeto(previo?.payload) ? (fila.tipo === 'pallet.unir' ? sinElReparto(previo.payload) : previo.payload) : {};
	return {
		raiz_id: raiz,
		entidad: fila.entidad,
		registro_id: fila.registro_id,
		tipo: funde ? previo.tipo : fila.tipo,
		payload: funde
			? { ...(previo.payload as Record<string, unknown>), ...(cambios as Record<string, unknown>) }
			: conserva
				? { ...identidad, ...previoConservado, ...(fila.payload as Record<string, unknown>) }
				: fila.payload,
		nro_viaje: null,
		...(numero === undefined ? {} : { numero }),
		personas: fila.personas,
		resumen: fila.resumen,
		sincronizado_en: sincronizadoEn,
	};
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
	return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/** El payload de una unión sin las claves de a partes (§12.21). */
function sinElReparto(payload: Record<string, unknown>): Record<string, unknown> {
	const { bolsas: _b, bolsas_vistas: _v, carga_version_id: _c, parte: _p, ...resto } = payload;
	return resto;
}

/**
 * Regla 14 (§5.2): un hijo no viaja hasta que su padre tenga acuse. Se QUITAN del
 * lote las filas cuyo padre siga pendiente, y el resto viaja igual.
 *
 * Quitar y no cortar en la primera bloqueada: si no, un silo trabado —su apertura
 * quedó en un epoch congelado por un 401— dejaba sin subir las paradas y los
 * cambios de carro que vinieran detrás, que no dependen de nada (Codex, ronda del
 * plan, 8/9). El orden entre lo que sí viaja se mantiene; lo quitado entra en el
 * lote siguiente, apenas su padre resuelva.
 */
export function filtrarPorDependencias(lote: FilaOutbox[], mutacionesPendientes: Set<string>): FilaOutbox[] {
	return lote.filter((f) => f.padre_id === undefined || !mutacionesPendientes.has(f.padre_id));
}

/** Un hijo pendiente cuyo padre todavía está en la cola. */
export interface EsperaDePadre {
	hijo: FilaOutbox;
	padre: FilaOutbox;
	/** El padre está en un epoch congelado (401 / re-enrolar): no viaja hasta que el encargado lo libere. */
	padreCongelado: boolean;
}

/**
 * Hijos pendientes cuyo padre sigue en la cola. El caso que el sync corta (lote vacío
 * tras re-enrolar, C1 de enrolar.ts) es `padreCongelado`: el enrolamiento viejo no se
 * reenvía solo, y «esperando señal» mentiría.
 */
export function esperasDePadre(pendientes: FilaOutbox[], epochsCongelados: Iterable<string>): EsperaDePadre[] {
	const porId = new Map(pendientes.map((f) => [f.id, f]));
	const congelados = new Set(epochsCongelados);
	const esperas: EsperaDePadre[] = [];
	for (const hijo of pendientes) {
		if (hijo.padre_id === undefined) continue;
		const padre = porId.get(hijo.padre_id);
		if (!padre) continue;
		esperas.push({ hijo, padre, padreCongelado: congelados.has(padre.epoch) });
	}
	return esperas;
}

export async function contarPendientes(bd: IDBDatabase): Promise<{ pendientes: number; incidentes: number; masViejaMs: number | null }> {
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	const pendientes = filas.filter((f) => f.estado_local === 'pendiente');
	const incidentes = filas.length - pendientes.length;
	const masVieja = pendientes.reduce<number | null>((min, f) => {
		const ms = Date.parse(f.creado_en);
		return Number.isNaN(ms) ? min : Math.min(min ?? ms, ms);
	}, null);
	return { pendientes: pendientes.length, incidentes, masViejaMs: masVieja };
}

/** Incidentes (rechazadas/conflictos) para la vista del encargado. */
export async function bandeja(bd: IDBDatabase): Promise<FilaOutbox[]> {
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	return filas.filter((f) => f.estado_local !== 'pendiente').sort((a, b) => (a.creado_en < b.creado_en ? -1 : 1));
}

/**
 * Los rótulos de las aperturas que están en la bandeja (§5.7 r. 22): siguen «tomados» en este teléfono
 * hasta que el árbol se rehaga o se quite. Si no, el número de un pallet que cayó colgando de otro se
 * volvía a proponer, y al rehacer el primero el segundo chocaba con esa apertura nueva sin que la
 * pantalla tuviera cómo cambiarle el rótulo (Codex, ronda de la v5 entera, 15/9).
 */
export async function rotulosEnBandeja(bd: IDBDatabase): Promise<Array<{ mutacion_id: string; campania_id: string; numero: number }>> {
	return (await bandeja(bd))
		.filter((f) => f.tipo === 'pallet.abrir')
		.flatMap((f) => {
			const numero = numeroDe(f.payload);
			const campania = campoDe(f.payload, 'campania_id');
			return numero === null || campania === null ? [] : [{ mutacion_id: f.id, campania_id: campania, numero }];
		});
}

/** Acuse del encargado: el incidente se elimina (ya está registrado en el server). */
export async function acusarIncidente(bd: IDBDatabase, id: string): Promise<void> {
	const tx = bd.transaction(['outbox', 'fotos'], 'readwrite');
	const fila = (await promesa(tx.objectStore('outbox').get(id))) as FilaOutbox | undefined;
	if (fila && fila.estado_local !== 'pendiente') {
		tx.objectStore('outbox').delete(id);
		// La foto del precinto de una apertura caída queda fallada en la misma transacción: sin la
		// fila del outbox nadie más podría saber que el silo no entró (Codex, ronda del diff, 14/9).
		if (fila.tipo === 'llenado.abrir') {
			const fotos = tx.objectStore('fotos');
			const todas = (await promesa(fotos.getAll())) as Array<{ foto_id: string; mutacion_id: string; falla?: unknown }>;
			for (const foto of todas) {
				if (foto.mutacion_id === id && !foto.falla) fotos.put({ ...foto, falla: { codigo: 0, motivo: 'entidad_rechazada', en: new Date().toISOString() } });
			}
		}
	}
	await alCompletar(tx);
}

export async function recibos(bd: IDBDatabase): Promise<Recibo[]> {
	const todos = (await promesa(bd.transaction('recibos').objectStore('recibos').getAll())) as Recibo[];
	return todos.sort((a, b) => (a.sincronizado_en < b.sincronizado_en ? 1 : -1));
}

/**
 * Lo cargado desde este teléfono en una jornada operativa: recibos (ya en la
 * oficina) más lo que todavía espera señal. Es el reporte chico que cada
 * persona pidió tener (visita 5/9, r. 17): carros y kilos, sin ir a la oficina.
 * Una corrección hecha desde gestión NO baja al teléfono: la pantalla lo dice.
 */
export async function cargadosEnLaJornada(
	bd: IDBDatabase,
	ahoraMs: number,
): Promise<{ fecha: string; recibos: Recibo[]; pendientes: FilaOutbox[] }> {
	const fecha = fechaDeProduccion(ahoraMs);
	const todos = await recibos(bd);
	const filas = (await promesa(bd.transaction('outbox').objectStore('outbox').getAll())) as FilaOutbox[];
	return {
		fecha,
		recibos: todos.filter((r) => r.resumen.fecha === fecha),
		pendientes: filas.filter((f) => f.estado_local === 'pendiente' && f.resumen.fecha === fecha),
	};
}

/**
 * Lo propio de verde desde una hora declarada (§5.5, verde reciente): los recibos de viaje y descarte y
 * las pendientes con hora del hecho igual o posterior a `desde`, con la forma que la proyección espera.
 * La hora sale del resumen (desde el 5/10) o, en un recibo de viaje de antes, de su payload (verde se
 * proyecta desde la v6 y lo conserva); un recibo de descarte de antes no la tiene, y queda null: la
 * proyección lo ordena por su acuse. Sin techo acá: lo fechado después de `hasta` lo cuenta la proyección.
 */
export async function propiosDeVerdeDesde(bd: IDBDatabase, desde: string): Promise<import('../nucleo/verde-reciente').RegistroPropio[]> {
	const tx = bd.transaction(['outbox', 'recibos']);
	const filas = (await promesa(tx.objectStore('outbox').getAll())) as FilaOutbox[];
	const comprobantes = (await promesa(tx.objectStore('recibos').getAll())) as Recibo[];
	const propios: import('../nucleo/verde-reciente').RegistroPropio[] = [];
	for (const r of comprobantes) {
		if (r.entidad !== 'viaje_verde' && r.entidad !== 'descarte') continue;
		const hora = horaDelHecho(r.resumen, r.payload);
		if (hora !== null && hora < desde) continue;
		propios.push({ ...formaPropia(r.entidad, r.resumen), raiz_id: r.raiz_id, registro_id: r.registro_id, hora, sincronizado_en: r.sincronizado_en, nro_viaje: r.nro_viaje, personas: r.personas, pendiente: false });
	}
	for (const f of filas) {
		if (f.estado_local !== 'pendiente' || (f.entidad !== 'viaje_verde' && f.entidad !== 'descarte')) continue;
		const hora = horaDelHecho(f.resumen, f.payload);
		if (hora !== null && hora < desde) continue;
		propios.push({ ...formaPropia(f.entidad, f.resumen), raiz_id: null, registro_id: f.registro_id, hora, nro_viaje: null, personas: f.personas, pendiente: true, id: f.id, epoch: f.epoch });
	}
	return propios;
}

/** La hora declarada de un viaje o un descarte, del resumen o del payload; null si ninguno la trae. */
function horaDelHecho(resumen: Resumen, payload: unknown): string | null {
	const delResumen = resumen.entidad === 'viaje_verde' ? resumen.entrada_en : resumen.entidad === 'descarte' ? resumen.registrado_en : undefined;
	if (typeof delResumen === 'string') return delResumen;
	if (typeof payload === 'object' && payload !== null) {
		const clave = resumen.entidad === 'viaje_verde' ? 'entrada_en' : 'registrado_en';
		const valor = (payload as Record<string, unknown>)[clave];
		if (typeof valor === 'string') return valor;
	}
	return null;
}

function formaPropia(entidad: 'viaje_verde' | 'descarte', resumen: Resumen): { entidad: 'viaje_verde' | 'descarte'; lote: string | null; carros: string; cant_carros: number; neto_g: number } {
	if (resumen.entidad === 'viaje_verde') return { entidad, lote: resumen.lote, carros: resumen.carros, cant_carros: resumen.cant_carros, neto_g: resumen.neto_g };
	return { entidad, lote: null, carros: '', cant_carros: 0, neto_g: resumen.entidad === 'descarte' ? resumen.neto_g : 0 };
}

type EntidadDeProduccion = 'llenado' | 'parcial' | 'parada' | 'cambio_carro';

function esDeProduccion(entidad: Entidad): entidad is EntidadDeProduccion {
	return ENTIDADES_PRODUCCION.has(entidad);
}

/**
 * Lo propio de producción desde una hora del hecho (§5.5, producción reciente): los recibos de silo, pesada,
 * parada y bajada y las pendientes, EN ORDEN DE APLICACIÓN (los recibos por su acuse y después las pendientes
 * por cola), con la hora del hecho sacada del payload: los recibos de producción lo conservan
 * (`ENTIDADES_PROYECTADAS`). El recibo de `llenado` es uno por silo y el del cierre pisa al de la apertura; un
 * descarte de un silo ya cerrado lleva `cerrado_en` null y queda sin hora: la proyección lo ordena por su acuse.
 * Sin techo acá: lo fechado después de `hasta` lo cuenta la proyección.
 */
export async function propiosDeProduccionDesde(bd: IDBDatabase, desde: string): Promise<import('../nucleo/produccion-reciente').PropioDeProduccion[]> {
	const tx = bd.transaction(['outbox', 'recibos']);
	const filas = (await promesa(tx.objectStore('outbox').getAll())) as FilaOutbox[];
	const comprobantes = (await promesa(tx.objectStore('recibos').getAll())) as Recibo[];
	const propios: import('../nucleo/produccion-reciente').PropioDeProduccion[] = [];
	for (const r of comprobantes.sort((a, b) => a.sincronizado_en.localeCompare(b.sincronizado_en))) {
		if (!esDeProduccion(r.entidad) || r.tipo === undefined) continue;
		const hora = horaDelHechoDeProduccion(r.tipo, r.payload);
		if (hora !== null && hora < desde) continue;
		propios.push({
			entidad: r.entidad,
			tipo: r.tipo,
			payload: r.payload,
			resumen: r.resumen as import('../nucleo/produccion-reciente').ResumenPropio,
			personas: r.personas,
			registro_id: r.registro_id,
			raiz_id: r.raiz_id,
			hora,
			sincronizado_en: r.sincronizado_en,
			pendiente: false,
		});
	}
	const pendientes = filas
		.filter((f) => f.estado_local === 'pendiente' && esDeProduccion(f.entidad))
		.sort((a, b) => (a.epoch === b.epoch ? a.seq - b.seq : a.epoch < b.epoch ? -1 : 1));
	for (const f of pendientes) {
		if (!esDeProduccion(f.entidad)) continue;
		const hora = horaDelHechoDeProduccion(f.tipo, f.payload);
		if (hora !== null && hora < desde) continue;
		propios.push({
			entidad: f.entidad,
			tipo: f.tipo,
			payload: f.payload,
			resumen: f.resumen as import('../nucleo/produccion-reciente').ResumenPropio,
			personas: f.personas,
			registro_id: f.registro_id,
			raiz_id: null,
			hora,
			pendiente: true,
			id: f.id,
			epoch: f.epoch,
		});
	}
	return propios;
}

/** La hora del hecho de una mutación de producción, del payload: la apertura, el cierre, el fin de la pesada, el turno de la parada o la bajada. */
function horaDelHechoDeProduccion(tipo: string, payload: unknown): string | null {
	const clave =
		tipo === 'llenado.abrir'
			? 'abierto_en'
			: tipo === 'llenado.cerrar' || tipo === 'llenado.descartar'
				? 'cerrado_en'
				: tipo === 'parcial.crear'
					? 'fin_en'
					: tipo === 'parada.crear' || tipo === 'cambio_carro.crear'
						? 'registrado_en'
						: null;
	if (clave === null || typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'string' ? valor : null;
}

const RETENCION_RECIBOS_DIAS = 30;

/**
 * Retención: recibos de más de 30 días de PRODUCCIÓN se purgan. Se compara por
 * el día de producción del registro, no por cuándo se sincronizó: un viaje
 * viejo que subió tarde no gana días de vida por eso.
 */
export async function purgarRecibosViejos(bd: IDBDatabase, ahoraMs: number, horaDelCatalogo?: string): Promise<void> {
	const limite = fechaDeProduccion(ahoraMs - RETENCION_RECIBOS_DIAS * 86_400_000);
	const tx = bd.transaction(['recibos', 'fotos'], 'readwrite');
	const store = tx.objectStore('recibos');
	const todos = (await promesa(store.getAll())) as Recibo[];
	// El recibo de un silo cuya foto todavía no subió es lo que la habilita a subir (§5.3): se
	// conserva mientras la foto espere, y se purga después.
	const fotos = (await promesa(tx.objectStore('fotos').getAll())) as Array<{ entidad: string; entidad_id: string; confirmada: boolean; falla?: unknown }>;
	const esperanFoto = new Set(fotos.filter((f) => f.confirmada && !f.falla).map((f) => `${f.entidad}/${f.entidad_id}`));
	for (const recibo of todos) {
		if (recibo.resumen.fecha >= limite) continue;
		if (esperanFoto.has(`${recibo.entidad}/${recibo.raiz_id}`)) continue;
		// Un efecto de producción que el snapshot de catálogos todavía no absorbió no es
		// historial: es lo que sostiene la pantalla (§5.1). Purgarlo resucitaría un silo
		// que este teléfono ya cerró (Codex, ronda del diff, 8/9).
		const sostieneLaPantalla =
			ENTIDADES_PROYECTADAS.has(recibo.entidad) && (horaDelCatalogo === undefined || recibo.sincronizado_en > horaDelCatalogo);
		if (sostieneLaPantalla) continue;
		store.delete([recibo.entidad, recibo.raiz_id]);
	}
	await alCompletar(tx);
}
